import { Heading, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';

import { ImmersiveModalWrapper } from 'src/components/dialogs/ImmersiveModalWrapper';
import { ResponsiveTabs } from 'src/components/ResponsiveTabs';
import {
  AssistantProvider,
  DRAWER_IDS,
  DRAWER_LABELS,
  DrawerId,
  MOBILE_DRAWER_LABELS,
} from 'src/context';
import { useFeatureFlags } from 'src/context/flagsContext';
import { ContextBar } from 'src/features/context-bar/ContextBar';
import { ContextBarAvatar } from 'src/features/context-bar/ContextBarAvatar';
import {
  ContextBarNavTabs,
  NavTab,
} from 'src/features/context-bar/ContextBarNavTabs';
import { getEntityName, useEntity } from 'src/features/context-bar/utils';
import { NoteModalProvider } from 'src/features/notes/NoteModal/noteModalContext';
import { ResizableGroup } from 'src/features/resizableWrapper/ResizableGroup';
import { ResizablePanel } from 'src/features/resizableWrapper/ResizablePanel';
import { ResizeHandle } from 'src/features/resizableWrapper/ResizeHandle';
import { useContextBar } from 'src/hooks/useContextBar';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { selectCurrentUser } from 'src/store/storeUtils';

type NavTabType = NavTab<DrawerId>;

const CONTEXT_BAR_PANEL_IDS = ['app-content', 'context-bar-content'];
const CONTEXT_BAR_MIN_PX = 288;
const CONTEXT_BAR_DEFAULT_PX = 396;
const CONTEXT_BAR_MAX_SIZE = '55%';
const CONTEXT_BAR_MOBILE_MIN_SIZE = '100%';

export const ContextBarLayout = ({
  children,
  contentMinWidth,
}: {
  children: ReactNode;
  contentMinWidth?: number;
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const hasAssistantTab = !currentUser?.isClientAccessLimited;
  const { EnableNewSidebar } = useFeatureFlags();

  const isMobileScreen = useMediaQuery('xs', 'max');
  const {
    open,
    setOpen,
    getActiveDrawer,
    pushDrawerHistory,
    target,
    isMobileLayout,
    drawerVisibilityConfig,
  } = useContextBar();
  const { entityType, id: entityId } = target ?? {};

  const navTabs = useMemo<NavTabType[]>(() => {
    const labelMap = isMobileScreen ? MOBILE_DRAWER_LABELS : DRAWER_LABELS;
    const tabs: NavTabType[] = [];

    if (drawerVisibilityConfig.DETAILS) {
      tabs.push({
        key: DRAWER_IDS.DETAILS,
        label: labelMap[DRAWER_IDS.DETAILS],
        icon: 'Details',
      });
    }

    if (drawerVisibilityConfig.NOTES) {
      tabs.push({
        key: DRAWER_IDS.NOTES,
        label: labelMap[DRAWER_IDS.NOTES],
        icon: 'Note',
      });
    }

    if (hasAssistantTab && drawerVisibilityConfig.INTERNAL_CHAT) {
      tabs.push({
        key: DRAWER_IDS.INTERNAL_CHAT,
        label: labelMap[DRAWER_IDS.INTERNAL_CHAT],
        icon: 'Comment',
      });
    }

    return tabs;
  }, [
    isMobileScreen,
    hasAssistantTab,
    drawerVisibilityConfig.DETAILS,
    drawerVisibilityConfig.NOTES,
    drawerVisibilityConfig.INTERNAL_CHAT,
  ]);

  const activeTabKey = getActiveDrawer(hasAssistantTab);

  const handleTabChange = (drawerId: DrawerId) => pushDrawerHistory(drawerId);

  const entity = useEntity(entityType, entityId);
  const entityName = getEntityName(entity);

  // One tree across the breakpoint: an iframe inside the page loses its
  // browsing context and reloads the embedded app if the element ever leaves
  // the document, so only the chrome around the page may change on a resize.
  return (
    <AssistantProvider>
      <NoteModalProvider>
        <ResizableGroup
          widthPersistKey="context-bar"
          persistPanelIds={CONTEXT_BAR_PANEL_IDS}
        >
          <ResizablePanel id="app-content" className="flex flex-col">
            <div
              className={clsx(
                'flex-1 min-h-0',
                !isMobileLayout && open && contentMinWidth && 'overflow-x-auto',
              )}
            >
              <div
                className="flex flex-col h-full"
                style={{
                  minWidth:
                    !isMobileLayout && open ? contentMinWidth : undefined,
                }}
              >
                {children}
              </div>
            </div>
          </ResizablePanel>
          {!isMobileLayout && target && (
            <>
              {open && (
                <>
                  <ResizeHandle />
                  <ResizablePanel
                    id="context-bar-content"
                    className={
                      EnableNewSidebar
                        ? 'box-border border-0 border-l border-solid border-non-hover-border'
                        : undefined
                    }
                    minSize={
                      isMobileScreen
                        ? CONTEXT_BAR_MOBILE_MIN_SIZE
                        : CONTEXT_BAR_MIN_PX
                    }
                    maxSize={isMobileScreen ? undefined : CONTEXT_BAR_MAX_SIZE}
                    defaultSize={
                      isMobileScreen ? undefined : CONTEXT_BAR_DEFAULT_PX
                    }
                    groupResizeBehavior="preserve-pixel-size"
                  >
                    <ContextBar />
                  </ResizablePanel>
                </>
              )}
              <div className="w-11 h-full flex flex-col items-center shrink-0 border-0 border-l border-solid border-non-hover-border bg-gray-100">
                {entityType && entityId && (
                  <ContextBarAvatar
                    className="my-4"
                    entityType={entityType}
                    entityId={entityId}
                  />
                )}
                <ContextBarNavTabs<DrawerId>
                  className="py-1.5"
                  tabs={navTabs}
                  activeTabKey={activeTabKey}
                  onTabChange={handleTabChange}
                />
              </div>
            </>
          )}
        </ResizableGroup>
        {isMobileLayout && target && (
          <ImmersiveModalWrapper isOpen={open}>
            <div className="h-[52px] pl-4 pr-3 flex items-center justify-between gap-2 grow-0 bg-gray-100">
              <div className="flex items-center gap-0.5 min-w-0">
                <IconButton
                  icon="ArrowLeft"
                  size="sm"
                  variant="minimal"
                  onClick={() => setOpen(false)}
                />
                <Heading className="truncate">{entityName}</Heading>
              </div>
              {entityType && entityId && (
                <ContextBarAvatar
                  className="my-4"
                  entityType={entityType}
                  entityId={entityId}
                />
              )}
            </div>
            <div className="grow-0">
              <ResponsiveTabs<DrawerId>
                overflow="scroll"
                tabs={navTabs as Array<Omit<NavTabType, 'icon'>>}
                activeTabKey={activeTabKey}
                onTabChange={handleTabChange}
                className="h-11 border border-solid border-l-0 border-r-0 border-non-hover-border box-border bg-gray-100"
              />
            </div>
            <div className="flex-1 min-h-0">
              <ContextBar />
            </div>
          </ImmersiveModalWrapper>
        )}
      </NoteModalProvider>
    </AssistantProvider>
  );
};
